import { useMutation } from "@apollo/client";
import { FC, useContext, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from 'react-select';
import { toast } from "react-toastify";
import { Col, Form, Input, Label, ModalHeader, Row, Spinner } from "reactstrap";
import CustomButton from "../../components/layout/CustomButton";
import { customSelectStyleNew } from "../../components/styled/customSelect";
import { AppContext } from "../../context";
import { CSVUploadFormType, UploadFormProps } from "../../interfaces";
import { downloadSignedUrlFile } from "../../utils/commonFn";
import { ADMIN_IMPORT_SOURCE, CSV_IMPORTS_HEADER, CSV_IMPORTS_SAMPLE_FILES, IMPORT_SOURCE, bucketFolders } from "../../utils/constant";
import { parseCSVFile, parseExcelFile, updateFileName, uploadFile, validateCSVbyHeaders } from "../../utils/helper";
import DragDropFile from "../candidate/candidate-components/DragDropFile";
import { CREATE_DATA_CSV_CONTACT_IMPORTS, CREATE_DATA_CSV_IMPORTS } from "./gql";
import { CONTACT_IMPORT_SAMPLE_DOWNLOAD_FILE_NAME, DOWNLOAD_CSV_SAMPLE, DOWNLOAD_MS_EXCEL_SAMPLE, IMPORT_CSV, START_UPLOADING_CSV, START_UPLOADING_EXCEL, STEP_1_IMPORT_CONTACTS } from "../../constants";


const UploadForm: FC<UploadFormProps> = ({ children, refetch, toggle, adminImport, isContactImport }) => {
  const { theme } = useContext(AppContext);
  const [file, setFile] = useState<File>()
  const [fileError, setFileError] = useState<string>()
  const [uploading, setUploading] = useState<boolean>(false)
  const [downloading, setDownloading] = useState<boolean>(false)
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)
  const { control, handleSubmit, getValues, setValue, formState: { errors } } = useForm<CSVUploadFormType>();
  const [createDataCsvImport, { loading: createLoader }] = useMutation(CREATE_DATA_CSV_IMPORTS)
  const [createDataCsvContactImport, { loading: createLoaderII }] = useMutation(CREATE_DATA_CSV_CONTACT_IMPORTS)

  const createImport = async ({ sourceType, endDate, startDate }: CSVUploadFormType) => {
    try {
      setUploading(true)
      const upload = await uploadFile(
        new File([file!], updateFileName(file!.name), { type: file!.type }),
        bucketFolders.csv,
        false, "attachment"
      );
      setUploading(false)
      if (upload?.status === 200) {
        const csvImport = await createDataCsvImport({
          variables: {
            createDataCsvImportInput: {
              sourceType, startDate, endDate,
              fileUrl: upload?.source,
              fileName: updateFileName(file!.name),
              adminImport
            }
          }
        })
        if (csvImport?.data?.createDataCsvImport?.status === 201) {
          toast.success(csvImport?.data?.createDataCsvImport?.message)
        }
        refetch && refetch()
        toggle && toggle()
      }
    } catch (error) {
      setUploading(false)
      console.log(error)
      toast.error("Something went wrong!")
    }
  }

  const createContactImport = async ({ startDate }: CSVUploadFormType) => {
    try {
      setUploading(true)
      const upload = await uploadFile(
        new File([file!], updateFileName(file!.name), { type: file!.type }),
        bucketFolders.csv,
        false, "attachment"
      );
      setUploading(false)
      if (upload?.status === 200) {
        const csvContactImport = await createDataCsvContactImport({
          variables: {
            createDataCsvContactImportInput: {
              startDate,
              fileUrl: upload?.source,
              fileName: updateFileName(file!.name),
              adminImport
            }
          }
        })
        if (csvContactImport?.data?.createDataCsvContactImport?.status === 201) {
          toast.success(csvContactImport?.data?.createDataCsvContactImport?.message)
        }
        refetch && refetch()
        toggle && toggle()
      }
    } catch (error) {
      setUploading(false)
      console.log(error)
      toast.error("Something went wrong!")
    }
  }

  const handleFileError = (message: string) => {
    setFileError(message);
    setButtonDisabled(true);
    setFile(undefined);
  };

  const validateHeaders = (headers: string[], sourceType: string): boolean => {
    if (sourceType === 'OUTREACH') {
      return CSV_IMPORTS_HEADER['OUTREACH'].every((header) => headers.includes(header));
    }

    if (sourceType === 'SEAM_LESS_ADMIN') {
      return (
        validateCSVbyHeaders(CSV_IMPORTS_HEADER['SEAM_LESS_ADMIN'], headers) ||
        CSV_IMPORTS_HEADER['SEAM_LESS_ADMIN'].length === headers.length
      );
    }

    return validateCSVbyHeaders(CSV_IMPORTS_HEADER[sourceType], headers);
  };

  const validateFile = async (file: File) => {
    const sourceType = getValues()?.sourceType;

    if (!isContactImport && !sourceType) {
      return handleFileError("Please Select Source First");
    }

    try {
      let headers: string[] | null = null;

      if (isContactImport) {
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
          return handleFileError("Invalid file type: Only .xlsx files are allowed for contact imports.");
        }
        headers = await parseExcelFile(file);
        const requiredHeaders = CSV_IMPORTS_HEADER['CONTACT_IMPORT'];
        const missingHeaders = requiredHeaders.filter((header) => !headers?.includes(header.trim()));
        if (missingHeaders.length > 0) {
          return handleFileError(`Invalid File: Missing columns - ${missingHeaders.join(', ')}`);
        }
      } else {
        headers = await parseCSVFile(file);
        if (!validateHeaders(headers!, sourceType)) {
          return handleFileError('Invalid File: File has missing columns');
        }
      }

      setFileError('');
      setButtonDisabled(false);
      setFile(file);
    } catch (errorMessage) {
      handleFileError(String(errorMessage));
    }
  };

  const handleSampleDownload = () => {
    const appEnv = process.env.REACT_APP_API_BASE_URL?.includes('api.ats') ? '_PROD' : ''
    if (getValues()?.sourceType) {
      setDownloading(true)
      downloadSignedUrlFile(CSV_IMPORTS_SAMPLE_FILES[`${getValues()?.sourceType + appEnv}`], `${getValues()?.sourceType}.csv`, setDownloading)
    }
    else if (isContactImport) {
      setDownloading(true)
      downloadSignedUrlFile(CSV_IMPORTS_SAMPLE_FILES['CONTACT_IMPORT' + appEnv], CONTACT_IMPORT_SAMPLE_DOWNLOAD_FILE_NAME, setDownloading)
    }
    else {
      if (!isContactImport) toast.warning("Please select source first")
      toast.warning("Unable to download Sample File")
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between mb-4">
        <ModalHeader className="pl-3">
          <Row className="align-items-center" >
            <div>{isContactImport ? STEP_1_IMPORT_CONTACTS : IMPORT_CSV}</div>
            {!adminImport && <button className="btn btn-sm btn-primary rounded-pill ml-3 "
              onClick={handleSampleDownload} style={{ fontSize: 13 }} >
              {downloading && <Spinner size="sm" style={{ color: "#eaa827" }} className="mr-2" />}
              {isContactImport ? DOWNLOAD_MS_EXCEL_SAMPLE : DOWNLOAD_CSV_SAMPLE}
            </button>}
          </Row>
        </ModalHeader>
      </div>
      <Form>
        <Col className="p-0">
          {!isContactImport && <Col className="my-3 p-0">
            <p className="font-weight-bold">Source <span className="text-danger">*</span></p>
            <Controller
              control={control}
              name="sourceType"
              rules={{
                required: {
                  value: true, message: 'Source should be selected!',
                }
              }}
              render={
                ({ field: { onChange } }) => (
                  <Select
                    placeholder="Source"
                    value={adminImport ?
                      ADMIN_IMPORT_SOURCE.find(({ value }) => value === getValues()?.sourceType)
                      : IMPORT_SOURCE.find(({ value }) => value === getValues()?.sourceType)}
                    isSearchable={false}
                    options={adminImport ? ADMIN_IMPORT_SOURCE : IMPORT_SOURCE}
                    onChange={(item) => {
                      onChange(item!.value)
                      if (file)
                        validateFile(file)
                    }}
                    styles={{ ...customSelectStyleNew(theme) }}
                  />)}
            />
            <small className="text-danger">
              {errors?.sourceType?.message}
            </small>
          </Col>}
          {
            getValues().sourceType === "OUTREACH" &&
            <>
              <Col className="my-3 p-0">
                <p className="font-weight-bold">Start Date <span className="text-danger">*</span></p>
                <Controller
                  name="startDate"
                  rules={{
                    required: {
                      value: getValues().sourceType === "OUTREACH",
                      message: 'Start date should be selected!'
                    }
                  }}
                  defaultValue={new Date().toISOString().substring(0, 10)}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="date"
                      onChange={(e) => {
                        setValue("startDate", e.target.value)
                      }}
                      max={new Date().toISOString().substring(0, 10)}
                    />
                  )}
                />
                <small className="text-danger">
                  {errors?.startDate?.message}
                </small>
              </Col>

              <Col className="my-3 p-0">
                <p className="font-weight-bold">End Date <span className="text-danger">*</span></p>
                <Controller
                  name="endDate"
                  rules={{
                    required: {
                      value: getValues().sourceType === "OUTREACH",
                      message: 'End date should be selected!'
                    }
                  }}
                  defaultValue={new Date().toISOString().substring(0, 10)}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="date"
                      onChange={(e) => {
                        setValue("endDate", e.target.value)
                      }}
                      max={new Date().toISOString().substring(0, 10)}
                    />
                  )}
                />
                <small className="text-danger">
                  {errors?.endDate?.message}
                </small>
              </Col>

            </>
          }
          <p className="font-weight-bold mb-2">{isContactImport ? START_UPLOADING_EXCEL : START_UPLOADING_CSV}<span className="text-danger">*</span></p>
          <Label className={fileError ? "text-danger" : "my-2 font-weight-normal"}>{fileError ? fileError : file?.name && `File Name: ${file.name}`}</Label>
          <Controller
            control={control} name="file"
            rules={{
              required: {
                value: true, message: 'File Should be selected!'
              }
            }}
            render={
              ({ field: { onChange } }) => (
                <Col style={{
                  // backgroundColor: "#F7FDFF",
                  // border: "1px dashed #2E95B4",
                  borderRadius: "5px"
                }}>
                  <DragDropFile
                    onUpload={(file: File) => {
                      onChange(file.name);
                      validateFile(file);
                      setFile(file);
                    }}
                  />
                </Col>)

            }
          />
          <small className="text-danger">
            {errors?.file?.message}
          </small>

          <div className="mt-3 d-flex w-40 align-self ml-auto" style={{ marginRight: '23px' }}>
            {children}
            <CustomButton
              className="ml-auto" disabled={false} loading={uploading || createLoader || createLoaderII}
              buttonClick={handleSubmit(isContactImport ? createContactImport : createImport)} buttonText="Upload File"
            />
          </div>
        </Col>
      </Form >
    </>
  )
}

export default UploadForm;